import React, { useState } from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Clock, CheckCircle, MapPin, Bell } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', company: '', phone: '',
    inquiry: '', message: '', consent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) { setError('Please agree to the privacy policy.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/contact/submit`, formData);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-16">
        <div className="container mx-auto px-6 max-w-2xl text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Message Received!</h1>
          <p className="text-lg text-gray-600 mb-8">
            Thank you for reaching out. Jan will review your message and get back to you within 1-2 business days.
          </p>
          <Button onClick={() => window.location.href = '/'} className="bg-blue-600 hover:bg-blue-700">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12" data-testid="contact-page">
      <PageSEO path="/contact" />
      <div className="container mx-auto px-6 max-w-5xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Contact Jan Pauwels — QRM Consulting</h1>
          <p className="text-lg text-gray-600">Have a question about QRM or want to discuss your manufacturing challenges? Let's talk.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Location</p>
                    <p className="text-sm text-gray-600">Belgium</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Bell className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <a href="mailto:info@smartqrmnavigator.com" className="text-sm text-blue-600 hover:underline">info@smartqrmnavigator.com</a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Response Time</p>
                    <p className="text-sm text-gray-600">Within 1-2 business days</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Why Reach Out?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>QRM implementation guidance</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>Strategic consulting inquiry</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>Partnership opportunities</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>General questions about QRM</span>
                </li>
              </ul>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-2 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Send a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cf-first">First Name *</Label>
                  <Input id="cf-first" data-testid="contact-firstname" required value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})} placeholder="Jan" />
                </div>
                <div>
                  <Label htmlFor="cf-last">Last Name *</Label>
                  <Input id="cf-last" data-testid="contact-lastname" required value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})} placeholder="Pauwels" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cf-email">Email *</Label>
                  <Input id="cf-email" type="email" data-testid="contact-email" required value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" />
                </div>
                <div>
                  <Label htmlFor="cf-phone">Phone</Label>
                  <Input id="cf-phone" data-testid="contact-phone" value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+32 ..." />
                </div>
              </div>
              <div>
                <Label htmlFor="cf-company">Company</Label>
                <Input id="cf-company" data-testid="contact-company" value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})} placeholder="Your company name" />
              </div>
              <div>
                <Label htmlFor="cf-inquiry">Inquiry Type *</Label>
                <Select value={formData.inquiry} onValueChange={(val) => setFormData({...formData, inquiry: val})}>
                  <SelectTrigger data-testid="contact-inquiry">
                    <SelectValue placeholder="Select inquiry type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="QRM Implementation">QRM Implementation</SelectItem>
                    <SelectItem value="Strategic Consulting">Strategic Consulting</SelectItem>
                    <SelectItem value="Partnership">Partnership Opportunity</SelectItem>
                    <SelectItem value="EBITDA Calculator">EBITDA Calculator Question</SelectItem>
                    <SelectItem value="Speaking Engagement">Speaking Engagement</SelectItem>
                    <SelectItem value="General Question">General Question</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cf-message">Message *</Label>
                <Textarea id="cf-message" data-testid="contact-message" required rows={5} value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell us about your manufacturing challenges or how we can help..." />
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox id="cf-consent" data-testid="contact-consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => setFormData({...formData, consent: checked})} />
                <Label htmlFor="cf-consent" className="text-sm text-gray-600 leading-tight">
                  I agree that my data will be processed to respond to my inquiry. We respect your privacy.
                </Label>
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <Button type="submit" data-testid="contact-submit-btn" disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3">
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
